import React, { useEffect, useRef, useState } from 'react';
import Dispatcher from '../../helpers/dispatcher';
import useFormEdit from '../../views/issue/forms/useFormEdit';
import { NavigationType, useNavigate, useBlocker } from 'react-router-dom';
import { flushLogs } from 'setup/logger/flushLogs';
import { hasFormsInEditing } from 'store/ui';

interface NavigationPromptState {
  action: null | NavigationType;
  isActive: boolean;
  customConfirm: boolean;
  onConfirm: () => void;
  nextLocation: { pathname: string | null };
}

const initState: NavigationPromptState = {
  action: null,
  isActive: false,
  customConfirm: false,
  onConfirm: (): void => {},
  nextLocation: { pathname: '/' },
};

export const leaveDialogDispatcher = new Dispatcher();

interface NavigationPromptProps {
  children: (arg: {
    isActive: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }) => React.ReactElement;
}

const NavigationPrompt = ({
  children,
}: NavigationPromptProps): null | React.ReactElement => {
  const [cState, setComponentState] =
    useState<NavigationPromptState>(initState);
  const _isMounted = useRef(false);
  const navigate = useNavigate();
  const { clearAllForms } = useFormEdit();
  const shouldBlock = (): boolean => hasFormsInEditing();

  const blocker = useBlocker(shouldBlock);

  const onBeforeUnload = (event: {
    returnValue: string;
  }): string | void => {
    if (!hasFormsInEditing()) {
      return;
    }

    const msg =
      'Do you want to leave this site?\n\nChanges you made may not be saved.';
    event.returnValue = msg;

    flushLogs();
    return msg;
  };

  useEffect(() => {
    leaveDialogDispatcher.addListener('show', showDialog);

    return (): void => {
      leaveDialogDispatcher.removeListener('show', showDialog);
    };
  }, []);

  const showDialog = ({ onConfirm }: { onConfirm: () => void }): void => {
    setComponentState((currentState) => ({
      ...currentState,
      onConfirm,
      isActive: true,
      customConfirm: true,
      nextLocation: { pathname: null },
    }));
  };

  useEffect(() => {
    _isMounted.current = true;
    window.addEventListener('beforeunload', onBeforeUnload);

    return (): void => {
      _isMounted.current = false;
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setComponentState((s) => ({
        ...s,
        isActive: true,
        nextLocation: { pathname: blocker.location?.pathname || null },
      }));
    }
  }, [blocker]);

  const navigateToNextLocation = (): void => {
    const { nextLocation } = cState;

    if (_isMounted.current) {
      setComponentState((componentState) => ({
        ...componentState,
        isActive: false,
      }));

      if (nextLocation.pathname) {
        clearAllForms();
        blocker.reset?.();
        navigate(nextLocation.pathname);
      }
    }
  };

  const onCancel = (): void => {
    setComponentState(initState);
  };

  const onConfirm = (): void => {
    if (cState.customConfirm) {
      setComponentState(initState);
      cState.onConfirm();
      return;
    }

    navigateToNextLocation();
  };

  if (!cState.isActive) {
    return null;
  }

  return children({ isActive: cState.isActive, onConfirm, onCancel });
};

export default NavigationPrompt;
